import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Button,
  Avatar,
  Divider,
  useMediaQuery,
  useTheme,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../../context/AuthContext';
import { ROUTES, ROUTE_CONFIG, FEATURE_FLAGS, APP_NAME } from '../../config/app.config';
import { LanguageSelector } from '../LanguageSelector';
import styles from './Navbar.module.css';

function NavLink({ path, label, t, location }) {
  const navigate = useNavigate();
  const isActive = location.pathname === path;
  
  return (
    <Button
      color="inherit"
      onClick={() => navigate(path)}
      sx={{
        color: isActive ? 'primary.main' : 'text.primary',
        fontWeight: isActive ? 600 : 400,
        textTransform: 'none',
        px: 2,
        '&:hover': {
          backgroundColor: 'action.hover',
          color: 'primary.main',
        },
      }}
    >
      {t(label)}
    </Button>
  );
}

export function Navbar() {
  const { t } = useTranslation();
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);

  const navRoutes = ROUTE_CONFIG.filter(
    (r) =>
      !r.public &&
      r.path !== ROUTES.LOGIN &&
      r.path !== ROUTES.HOME &&
      (!r.flag || FEATURE_FLAGS[r.flag]) &&
      (!r.roles?.length || (user && r.roles.includes(user.role)))
  );

  const handleMenu = (e) => setAnchorEl(e.currentTarget);
  const handleMobileMenu = (e) => setMobileMenuAnchor(e.currentTarget);
  const handleClose = () => {
    setAnchorEl(null);
    setMobileMenuAnchor(null);
  };
  
  const handleLogout = () => {
    handleClose();
    logout();
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: 'background.paper',
        color: 'text.primary',
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
      }}
    >
      <Toolbar sx={{ px: { xs: 2, md: 3 } }}>
        <Typography
          variant="h6"
          component="div"
          onClick={() => navigate(ROUTES.HOME)}
          sx={{
            fontWeight: 700,
            color: 'primary.main',
            cursor: 'pointer',
            mr: { xs: 1, md: 3 },
          }}
        >
          {APP_NAME}
        </Typography>

        {isAuthenticated && (
          <>
            {/* Desktop Navigation */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, flexGrow: 1 }}>
              {navRoutes.map((route) => (
                <NavLink
                  key={route.path}
                  path={route.path}
                  label={route.label}
                  t={t}
                  location={location}
                />
              ))}
            </Box>

            {/* Mobile Menu Button */}
            <IconButton
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={handleMobileMenu}
              sx={{ display: { xs: 'block', md: 'none' }, mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          </>
        )}

        <Box sx={{ flexGrow: { xs: 1, md: 0 } }} />

        {isAuthenticated ? (
          <>
            <LanguageSelector />
            <IconButton onClick={handleMenu} sx={{ ml: 1 }}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: 'primary.main',
                  fontSize: '0.875rem',
                }}
              >
                {getInitials(user?.username || user?.email || 'User')}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  minWidth: 200,
                  boxShadow: '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  borderRadius: 2,
                },
              }}
            >
              <MenuItem disabled>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {user?.username || user?.email || 'User'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user?.email || user?.role || ''}
                  </Typography>
                </Box>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('auth.logout')}</ListItemText>
              </MenuItem>
            </Menu>

            {/* Mobile Menu */}
            <Menu
              anchorEl={mobileMenuAnchor}
              open={Boolean(mobileMenuAnchor)}
              onClose={handleClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  minWidth: 200,
                  boxShadow: '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  borderRadius: 2,
                },
              }}
            >
              {navRoutes.map((route) => {
                const isActive = location.pathname === route.path;
                return (
                  <MenuItem
                    key={route.path}
                    onClick={() => {
                      navigate(route.path);
                      handleClose();
                    }}
                    selected={isActive}
                    sx={{
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? 'primary.main' : 'inherit',
                    }}
                  >
                    {t(route.label)}
                  </MenuItem>
                );
              })}
            </Menu>
          </>
        ) : (
          <>
            <LanguageSelector />
            <Button
              variant="contained"
              onClick={() => navigate(ROUTES.LOGIN)}
              sx={{ ml: 2 }}
            >
              {t('nav.login')}
            </Button>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
}
